import { useEffect, useState } from 'react';
import { MeansCash } from '../../libraries/transactions';
import { ICurrency } from '../../libraries/currencies';
import { Language } from '../languages/languageHandler';
import { IEntity } from '../../libraries/entity';
import { ILocation } from '../../libraries/location';
import store from '../client-redux/store';
import { toMoment } from './date';
import { EquipState } from '../../libraries/zps';

export const getEquipmentStateLabel = (state: EquipState, lang: Language): string => {
  switch (state) {
    case EquipState.IN_SERVICE:
      return lang.EQUIPMENT_STATUS_LABEL_ONLINE;

    case EquipState.IN_SERVICE_WITH_WARN:
      return lang.EQUIPMENT_STATUS_IN_SERVICE_WITH_WARN;

    case EquipState.OUT_OF_SERVICE:
      return lang.EQUIPMENT_STATE_BUTTON_OUT_OF_SERVICE;

    case EquipState.OFFLINE:
      return lang.EQUIPMENT_STATUS_LABEL_OFFLINE;

    case EquipState.DEFINED:
      return lang.EQUIPMENT_STATUS_LABEL_DEFINED;

    case EquipState.NOT_DEFINED:
      return lang.EQUIPMENT_STATUS_LABEL_NOT_DEFINED;

    default:
      return '-';
  }
};

export const roundUpToNextIncrement = (value: number, increment: number) => {
  return Math.ceil(value / increment) * increment;
};

export const roundDownToPrevIncrement = (value: number, increment: number) => {
  return Math.floor(value / increment) * increment;
};

export const delegationCheck = (sid: number | null | undefined, locations: ILocation[] = []): string => {
  if (!sid || !locations.length) return '';
  const location = locations.find(location => location.sid === sid);
  return location?.name || '';
};

export const formatCurrAmount = (
  amount: number,
  currency?: ICurrency,
  showDecimal: boolean = true,
  showSymbol: boolean = true
) => {
  if (!currency || !currency.factor) return 'Invalid currency';

  if (isNaN(amount)) return 'Invalid amount';

  //format "0.00 SIMBOL"
  let decimalAmount = amount * currency.factor;
  let formattedAmount = showDecimal ? decimalAmount.toFixed(2) : decimalAmount.toFixed(0);
  //let formattedAmount = decimalAmount.toFixed(2);
  //TODO: replace currencyStr for the currency symbol when on the database
  return showSymbol ? `${formattedAmount} ${currency.currencyStr}` : formattedAmount;
};

export const getCurrencyName = (cid?: number): string => {
  let currencySettings = store.getState().currencies.currencies;
  let found;
  if (currencySettings) {
    found = currencySettings.find(element => element.cid === cid);
  }
  return found ? found.currencyStr : `currencies_${cid}`;
};

export const getCurrencyValue = (
  cid?: number,
  cindex?: number,
  showDecimal: boolean = false,
  showSymbol: boolean = true
): string => {
  let currencySettings = store.getState().currencies.currencies;
  let found;

  if (currencySettings && cid !== undefined && cindex !== undefined) {
    found = currencySettings.find(element => element.cid === cid);

    if (found) {
      if (cindex >= 0 && cindex < found.mc_value.length) {
        const value = found.mc_value[cindex];
        return formatCurrAmount(value, found, showDecimal, showSymbol);
      }
    }
  }
  if (cid === 0) return 'n/d';
  return `currencies_${cid}`;
};

export const getEntityName = (entid?: number): string => {
  let entitySettings = store.getState().entities.entities;
  let found;
  if (entitySettings && entid !== undefined) {
    found = entitySettings.find(element => element.entid === entid);
  }
  return found ? found.name : '';
};

export const getLocationName = (sid?: number): string => {
  const locationSettings = store.getState().locations.locations;
  if (!locationSettings || !sid) return '';
  return locationSettings.find(element => element.sid === sid)?.name || '';
};

export function daysOrMinutesAgo(dateInput: string | Date, lang: Language): string {
  const date = toMoment(dateInput);
  if (!date.isValid()) {
    return '-';
  }

  const now = toMoment();
  const diffDays = now.diff(date, 'days');
  const diffMinutes = now.diff(date, 'minutes');

  if (isNaN(diffMinutes)) {
    console.warn('[daysOrMinutesAgo] diffMinutes is NaN');
    return '-';
  }

  if (diffMinutes >= 1381) {
    const days = Math.floor(diffMinutes / (60 * 24));
    return days === 1 ? ` ${lang.GLOBAL_DAY_AGO || 'day ago'}` : `${days} ${lang.GLOBAL_DAYS_AGO || 'days ago'}`;
  }

  if (diffDays === 0) {
    if (diffMinutes < 1) return lang.GLOBAL_TODAY || 'Today';
    if (diffMinutes < 60) {
      return diffMinutes === 1
        ? ` ${lang.GLOBAL_MINUTE_AGO || 'minute ago'}`
        : `${diffMinutes} ${lang.GLOBAL_MINUTES_AGO || 'minutes ago'}`;
    }

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    const roundedHours = minutes > 30 ? hours + 1 : hours;

    return `${roundedHours} ${lang.GLOBAL_HOURS_AGO || 'hours ago'}`;
  }

  return diffDays === 1 ? ` ${lang.GLOBAL_DAY_AGO || 'day ago'}` : `${diffDays} ${lang.GLOBAL_DAYS_AGO || 'days ago'}`;
}

interface WindowSize {
  width: number;
  height: number;
}

export function useWindowSize(): WindowSize {
  const [size, setSize] = useState<WindowSize>({
    width: 0,
    height: 0
  });

  useEffect(() => {
    function updateSize() {
      setSize({
        width: window.screen.width,
        height: window.screen.height
      });
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return size;
}

export const formatTransactionType = (lang: Language, type: string | number | undefined): string => {
  if (type === undefined || type === null) return '';
  const key = String(type);
  if (_ztransactionType[key] === undefined) return key;
  return lang[_ztransactionType[key].label];
};

export const formatShortTransactionType = (lang: Language, type: string | undefined): string => {
  if (!type) return '';
  if (_ztransactionType[type] === undefined) return type;

  return lang[_ztransactionType[type].label_min];
};

export const formatTransactionMethod = (lang: Language, valueType: string | undefined): string => {
  if (!valueType) return '';
  return lang[`VALUE_TYPE_${valueType}`] || valueType;
};

export const formatTransactionStatus = (lang: Language, status: string | undefined): string => {
  if (!status) return '';
  switch (status) {
    case 'WAITING': //normal deposit to be send
      return lang.TRANSACTIONS_STATUS_WAITING;
    case 'WILL_NOT_BE_SENT': //cancelled deposit/deposit with error and without values
      return lang.TRANSACTIONS_STATUS_WILL_NOT_BE_SENT;
    case 'SET_AS_NOT_TO_SEND': //Deposit ok without value, decided not to sent
      return lang.TRANSACTIONS_STATUS_SET_AS_NOT_TO_SEND;
    case 'SENDED_TO_COREBANKING':
      return lang.TRANSACTIONS_STATUS_SENDED_TO_COREBANKING;
    case 'CARD_CHECKED':
      return lang.TRANSACTIONS_STATUS_CARD_CHECKED;
    case 'DEPOSIT_PARCIAL_RECEIVED':
      return lang.TRANSACTIONS_STATUS_DEPOSIT_PARCIAL_RECEIVED;
    case 'WAITING_DECLARATION':
      return lang.TRANSACTIONS_STATUS_WAITING_DECLARATION;
    default:
      return status;
  }
};

export const handleEntid = (entities: IEntity[], entid: number): string | undefined => {
  if (!entities) return '';
  if (!entid) return '';

  let filteredEntity = entities.find(entity => entity.entid === entid);

  if (!filteredEntity) return '';

  return filteredEntity.name;
};

export const handleDownload = (data: string, filename: string, mimetype: string) => {
  var element = document.createElement('a');
  element.setAttribute('href', 'data:' + mimetype + ';base64,' + data);
  element.setAttribute('download', filename);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
};

export type IQuantities = {
  quantity: number;
  amount: any;
};

export const checkForZeroQuantity = (start: number, end: number, quantities: IQuantities[]): boolean => {
  for (let i = start; i < end; i++) {
    if (quantities[i]?.quantity > 0) return false;
  }
  return true;
};

export const prepareQuantities = (cash: MeansCash[], currency: ICurrency) => {
  if (!currency || !cash) return [];

  let quantities: IQuantities[] = [];

  for (let i = 0; i < currency.nCoins + currency.nBills; i++) {
    quantities.push({
      quantity: 0,
      amount: 0
    });
  }

  cash.forEach(cashItem => {
    quantities[cashItem.index].quantity = cashItem.quantity;
    quantities[cashItem.index].amount =
      cashItem.quantity * currency.denominations[cashItem.index].value * currency.factor;
    if (quantities[cashItem.index].amount % 1 !== 0) {
      quantities[cashItem.index].amount = quantities[cashItem.index].amount.toFixed(2);
    }
  });

  return quantities;
};

export function useFormattedCurrencyInput(rawValue: number, setRawValue: (val: number) => void, currency: ICurrency) {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatCurrAmount(rawValue || 0, currency, true, true));
    } else {
      setDisplayValue((rawValue || 0).toString());
    }
  }, [rawValue, currency, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    setDisplayValue((rawValue || 0).toString());
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawStr = event.currentTarget.value.replace(/[^0-9.,]/g, '').replace(',', '.');
    const parsed = parseFloat(rawStr);
    const raw = isNaN(parsed) ? 0 : parsed;
    setRawValue(raw);
    setDisplayValue(event.currentTarget.value);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return {
    displayValue,
    handleFocus,
    handleChange,
    handleBlur
  };
}

type iTransactionType = {
  [key: string]: { label: string; label_min: string };
  [key: number]: { label: string; label_min: string };
};

const _ztransactionType: iTransactionType = {
  '01010101': {
    label: 'OP_01010101_LABEL',
    label_min: 'OP_01010101_LABEL_MIN'
  },
  '01010122': {
    label: 'OP_01010122_LABEL',
    label_min: 'OP_01010122_LABEL_MIN'
  },
  '01010123': {
    label: 'OP_01010123_LABEL',
    label_min: 'OP_01010123_LABEL_MIN'
  },
  '01010125': {
    label: 'OP_01010125_LABEL',
    label_min: 'OP_01010125_LABEL_MIN'
  },
  '01010128': {
    label: 'OP_01010128_LABEL',
    label_min: 'OP_01010128_LABEL_MIN'
  },
  '01010301': {
    label: 'OP_01010301_LABEL',
    label_min: 'OP_01010301_LABEL_MIN'
  },
  '01010322': {
    label: 'OP_01010322_LABEL',
    label_min: 'OP_01010322_LABEL_MIN'
  },
  '01010328': {
    label: 'OP_01010328_LABEL',
    label_min: 'OP_01010328_LABEL_MIN'
  },
  '01010334': {
    label: 'OP_01010334_LABEL',
    label_min: 'OP_01010334_LABEL_MIN'
  },
  '01010401': {
    label: 'OP_01010401_LABEL',
    label_min: 'OP_01010401_LABEL_MIN'
  },
  '01011101': {
    label: 'OP_01011101_LABEL',
    label_min: 'OP_01011101_LABEL_MIN'
  },
  '01011122': {
    label: 'OP_01011122_LABEL',
    label_min: 'OP_01011122_LABEL_MIN'
  },
  '01011123': {
    label: 'OP_01011123_LABEL',
    label_min: 'OP_01011123_LABEL_MIN'
  },
  '01011128': {
    label: 'OP_01011128_LABEL',
    label_min: 'OP_01011128_LABEL_MIN'
  },
  '01011201': {
    label: 'OP_01011201_LABEL',
    label_min: 'OP_01011201_LABEL_MIN'
  },
  '01011222': {
    label: 'OP_01011222_LABEL',
    label_min: 'OP_01011222_LABEL_MIN'
  },
  '01011223': {
    label: 'OP_01011223_LABEL',
    label_min: 'OP_01011223_LABEL_MIN'
  },
  '01011228': {
    label: 'OP_01011228_LABEL',
    label_min: 'OP_01011228_LABEL_MIN'
  },
  '01010201': {
    label: 'OP_01010201_LABEL',
    label_min: 'OP_01010201_LABEL_MIN'
  },
  '01010202': {
    label: 'OP_01010202_LABEL',
    label_min: 'OP_01010202_LABEL_MIN'
  },
  '01010222': {
    label: 'OP_01010222_LABEL',
    label_min: 'OP_01010222_LABEL_MIN'
  },
  '01010224': {
    label: 'OP_01010224_LABEL',
    label_min: 'OP_01010224_LABEL_MIN'
  },
  '01010225': {
    label: 'OP_01010225_LABEL',
    label_min: 'OP_01010225_LABEL_MIN'
  },
  '01010226': {
    label: 'OP_01010226_LABEL',
    label_min: 'OP_01010226_LABEL_MIN'
  },
  '01010227': {
    label: 'OP_01010227_LABEL',
    label_min: 'OP_01010227_LABEL_MIN'
  },
  '01010228': {
    label: 'OP_01010228_LABEL',
    label_min: 'OP_01010228_LABEL_MIN'
  },
  '01010229': {
    label: 'OP_01010229_LABEL',
    label_min: 'OP_01010229_LABEL_MIN'
  },
  '01010230': {
    label: 'OP_01010230_LABEL',
    label_min: 'OP_01010230_LABEL_MIN'
  },
  '01010231': {
    label: 'OP_01010231_LABEL',
    label_min: 'OP_01010231_LABEL_MIN'
  },
  '01010232': {
    label: 'OP_01010232_LABEL',
    label_min: 'OP_01010232_LABEL_MIN'
  },
  '01010233': {
    label: 'OP_01010233_LABEL',
    label_min: 'OP_01010233_LABEL_MIN'
  },
  '01010901': {
    label: 'OP_01010901_LABEL',
    label_min: 'OP_01010901_LABEL_MIN'
  },
  '01011001': {
    label: 'OP_01011001_LABEL',
    label_min: 'OP_01011001_LABEL_MIN'
  },
  '01011301': {
    label: 'OP_01011301_LABEL',
    label_min: 'OP_01011301_LABEL_MIN'
  },
  '01011322': {
    //canceled change
    label: 'OP_01011325_LABEL',
    label_min: 'OP_01011325_LABEL_MIN'
  },
  '01011328': {
    //canceled change
    label: 'OP_01011325_LABEL',
    label_min: 'OP_01011325_LABEL_MIN'
  },
  '01011325': {
    //canceled change
    label: 'OP_01011325_LABEL',
    label_min: 'OP_01011325_LABEL_MIN'
  },
  '01011334': {
    label: 'OP_01011334_LABEL',
    label_min: 'OP_01011334_LABEL_MIN'
  },
  '02120503': {
    label: 'OP_02120503_LABEL',
    label_min: 'OP_02120503_LABEL_MIN'
  },
  '02120506': {
    label: 'OP_02120506_LABEL',
    label_min: 'OP_02120506_LABEL_MIN'
  },
  '01010223': {
    label: 'OP_01010223_LABEL',
    label_min: 'OP_01010223_LABEL_MIN'
  },
  '01012101': {
    label: 'OP_01012101_LABEL',
    label_min: 'OP_01012101_LABEL_MIN'
  },
  '01012122': {
    label: 'OP_01012122_LABEL',
    label_min: 'OP_01012122_LABEL_MIN'
  },
  '01010323': {
    label: 'OP_01010323_LABEL',
    label_min: 'OP_01010323_LABEL_MIN'
  },
  1: {
    label: 'OP_1_LABEL',
    label_min: 'OP_1_LABEL_MIN'
  },
  2: {
    label: 'OP_2_LABEL',
    label_min: 'OP_2_LABEL_MIN'
  },
  3: {
    label: 'OP_3_LABEL',
    label_min: 'OP_3_LABEL_MIN'
  },
  4: {
    label: 'OP_4_LABEL',
    label_min: 'OP_4_LABEL_MIN'
  },
  5: {
    label: 'OP_5_LABEL',
    label_min: 'OP_5_LABEL_MIN'
  },
  6: {
    label: 'OP_6_LABEL',
    label_min: 'OP_6_LABEL_MIN'
  },
  7: {
    label: 'OP_7_LABEL',
    label_min: 'OP_7_LABEL_MIN'
  },
  8: {
    label: 'OP_8_LABEL',
    label_min: 'OP_8_LABEL_MIN'
  },
  9: {
    label: 'OP_9_LABEL',
    label_min: 'OP_9_LABEL_MIN'
  },
  10: {
    label: 'OP_10_LABEL',
    label_min: 'OP_10_LABEL_MIN'
  }
};

export const calculateTotal = (mc: MeansCash[], currency?: ICurrency): number => {
  if (!currency || !mc) return 0;

  return mc.reduce((total, item) => {
    const denom = currency.denominations?.find(d => d.index === item.index);
    const value = denom ? denom.value : 0;
    return total + value * item.quantity;
  }, 0);
};

export const formatDocTypeName = (lang: Language, docType?: number | null) => {
  if (docType === 20) return lang.TRANSACTION_TYPE_FT;
  if (docType === 21) return lang.TRANSACTION_TYPE_TX;

  if (docType !== undefined && docType !== null) {
    return String(docType);
  }

  return '';
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };

    reader.onerror = error => reject(error);
  });
};
